import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Label } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import employeesService from "@/services/employeesService";

import PermissionsCard from "@/components/settings/employees/permissionsCard";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";
import CheckRole from "@/components/shared/checkRole";

import {
  emptyPermissionState,
  permissionStateFromRoles,
} from "@/lib/employeePermissions";

import * as z from "zod";

const CreateEmployee = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [permissions, setPermissions] = useState(emptyPermissionState);
  const [merchantId, setMerchantId] = useState(null);
  const [type, setType] = useState("");
  const [sellerCode, setSellerCode] = useState("");

  const { toast } = useToast();

  useEffect(() => {
    employeesService.getMerchantUserById(id).then((res) => {
      form.setValue("firstName", res.firstName);
      form.setValue("lastName", res.lastName);
      form.setValue("email", res.email);
      form.setValue("phone", res.phone);
      setMerchantId(res.id);
      setType(res.type || "");
      setSellerCode(res.sellerCode || "");

      setPermissions(permissionStateFromRoles(res.roles));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    mode: "onBlur",
    resolver: zodResolver(
      z.object({
        firstName: z.string().nonempty({ message: t("common.nonEmpty") }),
        lastName: z.string().nonempty({ message: t("common.nonEmpty") }),
      }),
    ),
    defaultValues: {
      firstName: "",
      lastName: "",
    },
  });

  const onSubmit = (data) => {
    const bodyObj1 = {
      merchantUserId: merchantId,
      ...data,
    };

    const bodyObj2 = {
      merchantUserId: merchantId,
      roles: permissions,
    };

    employeesService
      .updateMerchantUser({ bodyObj1, bodyObj2 })
      .then((res) => {
        if (!Array.isArray(res) || res.some((r) => r.status !== 200)) {
          toast({
            title: t("settingsPage.employeeUpdateError"),
            variant: "error",
          });
        } else {
          toast({
            title: t("settingsPage.employeeUpdatedSuccessfully"),
            variant: "success",
          });
        }
      })
      .catch((err) => {
        toast({
          title:
            err.response?.data?.errors?.[0]?.message ||
            t("settingsPage.employeeUpdateError"),
          variant: "error",
        });
      });
  };

  return (
    <main className="flex min-h-screen flex-col gap-5 bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/settings/employees">
          {t("settingsPage.employees")}
        </BreadcrumbItem>
        <BreadcrumbItem active>{t("settingsPage.editEmployee")}</BreadcrumbItem>
      </Breadcrumb>
      <div className="container mx-auto flex flex-col items-center justify-center gap-6">
        <div className="formBox flex-col gap-4">
          <h2 className="mb-5 font-medium">{t("settingsPage.employeeInfo")}</h2>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid w-full grid-cols-1 gap-4 md:grid-cols-2"
            >
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.firstName")}</Label>
                    <FormControl>
                      <Input placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.lastName")}</Label>
                    <FormControl>
                      <Input placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.phone")}</Label>
                    <FormControl>
                      <Input disabled placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.email")}</Label>
                    <FormControl>
                      <Input disabled placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {type === "STORE" && (
                <FormField
                  control={form.control}
                  name="sellerCode"
                  render={() => (
                    <FormItem>
                      <Label>{t("settingsPage.sellerCode", "Bayi Kodu")}</Label>
                      <FormControl>
                        <Input disabled placeholder="" value={sellerCode} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </form>
          </Form>
        </div>
      </div>
      <div className="container mx-auto flex flex-col items-center justify-center gap-6">
        <PermissionsCard permissions={permissions} onChange={setPermissions} />
        <CheckRole section="PERSONALS" action="EDIT">
          <Button
            className="mb-20 ml-auto"
            onClick={form.handleSubmit(onSubmit)}
          >
            {t("common.save")}
          </Button>
        </CheckRole>
      </div>
    </main>
  );
};

export default CreateEmployee;
